import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
    BatterySetDto,
    BatterySetService,
    CompleteSortieCommand,
    RpaDto,
    CraftService,
    DisplayableMissionDto,
    EquipmentDto,
    EquipmentService,
    FlyFreelyError,
    FlyFreelyLoggingService,
    InUseMissionWorkflowVersionDto,
    MissionRoleDto,
    MissionRoleService,
    MissionService,
    MissionWorkflowService,
    PersonService,
    RpaTypeDto,
    RpaTypesService,
    SortieDto,
    UpdateRetrospectiveMissionCommand,
    WorkTracker,
    fromTimestamp,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import * as moment from 'moment';
import { BehaviorSubject, ReplaySubject, Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { checkFlightsSorted, getEffectiveStartTime } from '../helpers';
import { MissionCompletionOrRecordService } from '../mission-completion/unassigned-mission-flight-logs/interfaces';

export interface MissionRecordFormModel {
    name: FormControl<string>;
    projectId: FormControl<number>;
    completionMessage: FormControl<string>;
    locationId: FormControl<number>;
    missionDate: FormControl<Date>;
    actualStartTime: FormControl<Date>;
    actualEndTime: FormControl<Date>;
    missionObjective: FormControl<string>;
    missionObjectiveTypeId: FormControl<number>;
    missionTypeId: FormControl<number>;
    missionWorkflowVersionId: FormControl<number>;
    rpicId: FormControl<number>;
    objectiveOutcomeNotes: FormControl<string>;
    outcome: FormControl<UpdateRetrospectiveMissionCommand.Outcome>;
    flights: FormArray<FormControl<CompleteSortieCommand>>;
    timeOfDay: FormControl<UpdateRetrospectiveMissionCommand.TimeOfDay>;
    timeZone: FormControl<string>;
    visualLineOfSight: FormControl<UpdateRetrospectiveMissionCommand.VisualLineOfSight>;
    journal: FormControl<string>;
    documentation: FormControl<any>;
}

export interface MissionRecordModel {
    name: string;
    projectId: number;
    completionMessage: string;
    locationId: number;
    missionDate: Date;
    actualStartTime: Date;
    actualEndTime: Date;
    missionObjective: string;
    missionObjectiveTypeId: number;
    missionTypeId: number;
    missionWorkflowVersionId: number;
    rpicId: number;
    objectiveOutcomeNotes: string;
    outcome: UpdateRetrospectiveMissionCommand.Outcome;
    flights: CompleteSortieCommand[];
    timeOfDay: UpdateRetrospectiveMissionCommand.TimeOfDay;
    timeZone: string;
    visualLineOfSight: UpdateRetrospectiveMissionCommand.VisualLineOfSight;
    journal: string;
    documentation: any;
}

export interface DisplayableResource {
    id: number;
    name: string;
}

@Injectable()
export class MissionRecordService extends MissionCompletionOrRecordService<MissionRecordFormModel> {
    public form: FormGroup<MissionRecordFormModel>;

    private availableRpaSubject = new ReplaySubject<RpaDto[]>();
    private allRpaTypesSubject = new ReplaySubject<RpaTypeDto[]>();
    private availablePersonnelSubject = new ReplaySubject<
        DisplayableResource[]
    >();
    private availableBatterySetsSubject = new ReplaySubject<BatterySetDto[]>();
    private availableEquipmentSubject = new ReplaySubject<EquipmentDto[]>();
    private missionWorkflowsSource = new ReplaySubject<
        InUseMissionWorkflowVersionDto[]
    >(1);
    private missionRolesSubject = new ReplaySubject<MissionRoleDto[]>(1);
    private allRpaSubject = new ReplaySubject<RpaDto[]>(1);
    private allBatterySetsSubject = new ReplaySubject<BatterySetDto[]>(1);
    missionWorkflows$ = this.missionWorkflowsSource.asObservable();
    availableRpas$ = this.availableRpaSubject.asObservable();
    allRpaTypes$ = this.allRpaTypesSubject.asObservable();
    availablePersonnel$ = this.availablePersonnelSubject.asObservable();
    availableBatterySets$ = this.availableBatterySetsSubject.asObservable();
    availableEquipment$ = this.availableEquipmentSubject.asObservable();
    missionRoles$ = this.missionRolesSubject.asObservable();
    allRpas$ = this.allRpaSubject.asObservable();
    allBatterySets$ = this.allBatterySetsSubject.asObservable();

    crewRoles: MissionRoleDto[];
    rpicRole: MissionRoleDto;
    missionWorkflows: InUseMissionWorkflowVersionDto[];

    private savedMissionSubject = new ReplaySubject<DisplayableMissionDto>(1);
    savedMission$ = this.savedMissionSubject.asObservable();

    private get flights() {
        return this.form.controls.flights;
    }

    flightsSorted = true;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    working$ = this.workTracker.observable;

    constructor(
        private missionService: MissionService,
        private missionRoleService: MissionRoleService,
        private personService: PersonService,
        private craftService: CraftService,
        private rpaTypesService: RpaTypesService,
        private batterySetService: BatterySetService,
        private equipmentService: EquipmentService,
        private missionWorkflowService: MissionWorkflowService,
        private logging: FlyFreelyLoggingService
    ) {
        super();
        this.form = new FormGroup<MissionRecordFormModel>({
            name: new FormControl(
                undefined,
                Validators.compose([
                    Validators.minLength(3),
                    Validators.required
                ])
            ),
            projectId: new FormControl(undefined),
            missionObjectiveTypeId: new FormControl(undefined),
            missionObjective: new FormControl(undefined),
            locationId: new FormControl(undefined),
            missionDate: new FormControl(undefined),
            timeZone: new FormControl(undefined),
            timeOfDay: new FormControl(undefined),
            visualLineOfSight: new FormControl(undefined),
            completionMessage: new FormControl(undefined),
            missionTypeId: new FormControl(undefined),
            missionWorkflowVersionId: new FormControl(undefined),
            rpicId: new FormControl(undefined),
            objectiveOutcomeNotes: new FormControl(undefined),
            outcome: new FormControl(undefined, Validators.required),
            flights: new FormArray([]),
            journal: new FormControl(undefined),
            documentation: new FormControl(undefined),
            actualStartTime: new FormControl(undefined),
            actualEndTime: new FormControl(undefined)
        });
    }

    ngOnDestroy() {
        this.availablePersonnelSubject.complete();
        this.availableBatterySetsSubject.complete();
        this.availableEquipmentSubject.complete();
        this.availableRpaSubject.complete();
        this.missionWorkflowsSource.complete();
        this.missionRolesSubject.complete();
        this.allRpaSubject.complete();
        this.allRpaTypesSubject.complete();
        this.allBatterySetsSubject.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    updateFlights(flights: SortieDto[]) {
        flights.forEach((f, ix) => {
            const value = this.flights.controls[ix].value;
            if (f.durationSource === SortieDto.DurationSource.LOGS) {
                this.flights.controls[ix].patchValue({
                    ...value,
                    durationSource: f.durationSource,
                    duration: f.duration
                });
            } else {
                this.flights.controls[ix].patchValue({
                    ...value,
                    durationSource: f.durationSource
                });
            }
        });
    }

    setupMissionForm(mission: DisplayableMissionDto, organisationId: number) {
        this.missionRoleService
            .findMissionRoles(organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .toPromise()
            .then(
                roles => {
                    this.crewRoles = roles.filter(
                        r =>
                            r.coreRole !==
                            MissionRoleDto.CoreRole.PILOT_IN_COMMAND
                    );
                    this.rpicRole = roles.filter(
                        r =>
                            r.coreRole ===
                            MissionRoleDto.CoreRole.PILOT_IN_COMMAND
                    )[0];
                    this.missionRolesSubject.next(roles);
                },
                error => this.logging.error(error)
            )
            .then(() => {
                this.form.patchValue({
                    name: mission.name,
                    projectId:
                        mission.project != null ? mission.project.id : null,
                    missionObjectiveTypeId:
                        mission.missionObjectiveType != null
                            ? mission.missionObjectiveType.id
                            : null,
                    missionObjective: mission.missionObjective,
                    locationId: mission.locationId,
                    timeZone: mission.timeZone,
                    missionDate:
                        mission.timeZone != null
                            ? mission.missionDate
                                ? fromTimestamp(
                                      mission.missionDate,
                                      mission.timeZone
                                  )
                                : moment()
                                      .hours(8)
                                      .minute(0)
                                      .seconds(0)
                                      .toDate()
                            : null,
                    timeOfDay: mission.timeOfDay,
                    visualLineOfSight: mission.visualLineOfSight,
                    completionMessage: mission.completionMessage,
                    missionTypeId:
                        mission.missionType != null
                            ? mission.missionType.id
                            : null,
                    missionWorkflowVersionId: mission.missionWorkflowVersionId,
                    rpicId: mission.missionCrewDetails.find(
                        c =>
                            c.role?.coreRole ===
                            MissionRoleDto.CoreRole.PILOT_IN_COMMAND
                    )?.person?.id,
                    objectiveOutcomeNotes: mission.objectiveOutcomeNotes,
                    outcome: mission.outcome,
                    journal: mission.completionMessage,
                    actualStartTime: fromTimestamp(
                        mission.actualStartTime ?? mission.missionDate,
                        mission.timeZone
                    ),
                    actualEndTime: fromTimestamp(
                        mission.actualEndTime ??
                            moment(mission.missionDate)
                                .add(mission.missionEstimatedTime, 'seconds')
                                .toISOString(),
                        mission.timeZone
                    )
                });
                while (this.form.controls.flights.length > 0) {
                    this.form.controls.flights.removeAt(0);
                }
                mission.sorties.forEach(sortie => {
                    this.addFlight(sortie);
                });
                this.flightsSorted = checkFlightsSorted(this.flights.value);
                this.flights.valueChanges
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe(values => {
                        this.flightsSorted = checkFlightsSorted(values);
                    });
                this.form.markAsPristine();
                this.form.updateValueAndValidity();
                this.refreshData(mission, organisationId);
                this.savedMissionSubject.next(mission);
            });

        this.form.controls.missionDate.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(date => {
                this.form.controls.missionDate.setValue(date, {
                    onlySelf: true,
                    emitEvent: false,
                    emitModelToViewChange: true
                });
            });
    }

    refreshData(mission: DisplayableMissionDto, organisationId: number) {
        forkJoin([
            this.craftService.findCrafts(organisationId),
            this.rpaTypesService.findRpaTypes(organisationId),
            this.batterySetService.findBatterySets(
                organisationId,
                mission.missionDate
            ),
            this.equipmentService.find(organisationId),
            this.missionWorkflowService.findActiveWorkflows(
                mission.organisationId,
                mission.missionDate
            )
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: ([
                    rpas,
                    rpaTypes,
                    batterySets,
                    equipment,
                    missionWorkflows
                ]) => {
                    this.allRpaSubject.next(rpas);
                    this.allRpaTypesSubject.next(rpaTypes);
                    this.allBatterySetsSubject.next(batterySets);
                    // Available personnel is the personnel currently assigned to the mission plan, which doesn't exist for retro missions.
                    this.availablePersonnelSubject.next([]);
                    this.availableRpaSubject.next(rpas);
                    this.availableBatterySetsSubject.next(batterySets);
                    this.availableEquipmentSubject.next(equipment);
                    this.missionWorkflows = missionWorkflows;
                    this.missionWorkflowsSource.next(missionWorkflows);
                    this.form.controls.missionWorkflowVersionId.updateValueAndValidity(
                        {
                            onlySelf: true
                        }
                    );
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while fetching resources: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    private createFlightControl(s: SortieDto) {
        return new FormControl<CompleteSortieCommand>(
            // @ts-ignore - missing type overlap not needed
            s,
            [Validators.required]
        );
    }

    updateMissionFromSortieCommand(
        sortieCommand: CompleteSortieCommand[],
        mission: DisplayableMissionDto
    ) {
        const values = this.form.value;
        const command: UpdateRetrospectiveMissionCommand = {
            ...values,
            name: values.name ?? mission.name,
            completionMessage:
                values.completionMessage ?? mission.completionMessage,
            sorties: sortieCommand,
            missionDate: toTimestamp(values.missionDate, values.timeZone),
            craftIds: [],
            equipmentIds: [],
            missionCrew: []
        };

        return this.missionService.updateHistoricalMission(mission.id, command);
    }

    addFlight(s: SortieDto): void {
        const sorties = this.form.controls.flights;
        sorties.push(this.createFlightControl(s));
    }

    updateFlight(flight: SortieDto) {
        if (
            flight == null ||
            this.flights == null ||
            this.flights.length === 0
        ) {
            return;
        }
        for (let ix = 0; ix < this.flights.length; ix++) {
            const currentValue = this.flights.at(ix).value;
            if (flight.id === currentValue.id) {
                this.flights.at(ix).setValue({
                    ...currentValue,
                    status: flight.status,
                    craftId: flight.craftId,
                    duration: flight.duration,
                    durationSource: flight.durationSource,
                    batterySetId: flight.batterySetId
                });
                return;
            }
        }
        // FIXME maybe create the flight if it doesn't exist?
    }

    deleteFlight(index: number) {
        this.form.controls.flights.removeAt(index);
    }

    sortFlights(): void {
        const flights = this.form.controls.flights;
        const sorted = [...flights.controls].sort((a, b) => {
            const timeA = getEffectiveStartTime(a.value);
            const timeB = getEffectiveStartTime(b.value);
            if (timeA == null && timeB == null) {
                return 0;
            }
            if (timeA == null) {
                return 1;
            }
            if (timeB == null) {
                return -1;
            }
            return timeA.getTime() - timeB.getTime();
        });
        flights.clear();
        sorted.forEach(c => flights.push(c));
    }

    noObjectivesRequired() {
        this.form.controls.missionObjective.clearValidators();
        this.form.controls.missionObjectiveTypeId.clearValidators();
        this.form.updateValueAndValidity();
    }
}
